import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import useStore from "../../helpers/useStore";
import { differenceInMinutes, format } from "date-fns";

import { PatientsContext } from "../../stores/Patients/patients.provider";
import { observer } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { CalendarCommands } from "../../stores/Calendar/calendar.commands";
import { CalendarContext } from "../../stores/Calendar/calendar.provider";
import { Cancel, EditCalendar, ErrorOutline, Smartphone } from "@mui/icons-material";
import { attentionColors, mobiliTheme } from "../../themes/mobiliTheme";
import moment from "moment";
import { VisitTypeBadge } from "../../atoms/Chip";
import { MobiliAvatar } from "../../atoms/MobiliAvatar";
import { CancelledAppointmentDto, UserDto } from "../../api-client";
import { useCancelledAppointments } from "../../stores/Appointment/appointment.queries";
import { useLocationId } from "../../stores/User/User.hooks";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { PATIENT_SUBTABS } from "../../stores/Patients/patientsStore";
import {
  useUpdateCancelledAppointment,
  useCancellationOfCancelledAppointment,
} from "../../stores/Appointment/appointment.mutation";
import { groupCancelledAppointmentsBySignature } from "../../helpers/appointmentGrouping";
import { slotTherapyLabel, slotTherapyList, slotsInChronologicalOrder } from "../../helpers/slotTherapy";
import { GapDays } from "../../molecules/GapDays";

const callStatusOptions = [
  { label: "Keine Angabe", status: "" },
  { label: "Nicht benötigt", status: "nicht benötigt" },
  { label: "Benötigt", status: "benötigt" },
  { label: "Versucht", status: "versucht" },
];

/**
 * Hausbesuch vs Praxispatient: the two are worked as separate processes, so the
 * list can be narrowed to one of them.
 *
 * The address carries three types — Praxis, Haus and Heim. Reception's split is
 * "does someone travel to the patient", so Heim (care home) belongs with Haus
 * rather than getting a third button.
 */
const VISIT_FILTERS = [
  { value: "alle", label: "Alle" },
  { value: "haus", label: "Hausbesuch" },
  { value: "praxis", label: "Praxispatient" },
] as const;

type VisitFilter = (typeof VISIT_FILTERS)[number]["value"];

/**
 * Rows whose address type is missing (the table already renders an error glyph
 * for them) are deliberately only reachable under "Alle" — we cannot say which
 * process they belong to, and quietly filing them under one would hide them
 * from the other.
 */
const matchesVisitFilter = (cancelled: any, filter: VisitFilter): boolean => {
  if (filter === "alle") return true;
  const addressType = cancelled?.appointment?.address?.type;
  return filter === "praxis"
    ? addressType === "Praxis"
    : addressType === "Haus" || addressType === "Heim";
};

/**
 * The Heilmittel cell: therapy short codes as plain text, clipped to the
 * cell — the table's grid already delimits the values, so the former per-slot
 * pill chips only added drawn boxes (design-system: no pills in table cells).
 * Rx numbers stay in the tooltip.
 */
const TherapyCellText = ({
  timeSlots,
  stackCount,
}: {
  timeSlots: any[];
  stackCount?: number;
}) => (
  <div className="flex min-w-0 items-center gap-1">
    <Tooltip
      title={slotsInChronologicalOrder(timeSlots)
        .map(
          (timeSlot: any) =>
            `${slotTherapyLabel(timeSlot)} ${timeSlot.therapyRx?.rx?.rxNumber || "Verkauf"}`
        )
        .join(", ")}
    >
      <span className="block min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-dense font-semibold text-ink-text">
        {slotTherapyList(timeSlots, " · ")}
      </span>
    </Tooltip>
    {stackCount !== undefined && stackCount > 1 && (
      <Tooltip title={`${stackCount} gleiche Termine`}>
        <span className="shrink-0 text-dense font-semibold tabular-nums text-ink-muted">
          ×{stackCount}
        </span>
      </Tooltip>
    )}
  </div>
);

const CancelledAppointments = observer(() => {
  const navigate = useNavigate();
  const calendarStore = useStore(CalendarContext);
  const { navigateToCalendar } = CalendarCommands(calendarStore);
  const PatientStore = useStore(PatientsContext);
  const { navigateToPatient } = PatientCommands(PatientStore);

  const locationId = useLocationId();

  const { data } = useCancelledAppointments(locationId);

  const cancelledAppointments = (data || []) as CancelledAppointmentDto[];

  // Held in the (app-wide) calendar store so the chosen view survives acting
  // on a row AND leaving/re-entering the dashboard within the same working
  // session (MD-290 D-DASH-01 retest finding) — the list is worked one filter
  // at a time.
  const visitFilter = calendarStore.cancelledVisitFilter as VisitFilter;
  const setVisitFilter = calendarStore.setCancelledVisitFilter;

  const visibleAppointments = useMemo(
    () => cancelledAppointments.filter((ca) => matchesVisitFilter(ca, visitFilter)),
    [cancelledAppointments, visitFilter]
  );

  const updateCancelledAppointment = useUpdateCancelledAppointment({ locationId: locationId! });

  const { mutateAsync: cancellationOfCancelledAppointment } =
    useCancellationOfCancelledAppointment();

  const [callStatusMap, setCallStatusMap] = useState<{ [key: string]: string }>({});
  const [selectedAppointmentId, setSelectedAppointmentId] = useState("");
  const [loadingAppointments, setLoadingAppointments] = useState<{ [key: string]: boolean }>({});

  const handlePatientData = (patient: any) => {
    navigateToPatient(navigate, patient.id, null, PATIENT_SUBTABS.APPOINTMENTS);
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event: any, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedAppointmentId(id); // Store the ID of the selected appointment
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Update call status for a specific cancelled appointment
  const handleCallStatusSelect = async (status: string) => {
    try {
      updateCancelledAppointment.mutate({
        id: selectedAppointmentId,
        dto: {
          callStatus: status,
        },
      });

      setCallStatusMap((prev) => ({ ...prev, [selectedAppointmentId]: status }));
      handleClose();
    } catch (error) {
      console.error("Error updating cancelled appointment:", error);
    }
  };

  const handlePlanning = (patientId: string, date: string) => {
    navigateToCalendar(navigate, {
      patientId: Number(patientId),
      date: date,
    });
  };

  const renderAppointmentTable = () => {
    const calculateColor = (originalDate: any) => {
      const differenceMinutes = differenceInMinutes(new Date(originalDate), new Date());
      if (differenceMinutes <= 0) {
        return attentionColors.immediateAttention;
      } else if (differenceMinutes <= 120) {
        return attentionColors.highPriority;
      } else if (differenceMinutes <= 240) {
        return attentionColors.mediumPriority;
      } else if (differenceMinutes <= 1440) {
        return attentionColors.lowPriority;
      } else {
        return attentionColors.customInfoPrimary;
      }
    };

    const cancellationOfCancelledAppointmentOnClick = async (
      cancelledAppointmentId: string,
      patientId: number
    ) => {
      setLoadingAppointments((prev) => ({
        ...prev,
        [cancelledAppointmentId]: true,
      }));

      try {
        await cancellationOfCancelledAppointment({
          appointmentId: cancelledAppointmentId,
          resolved: true,
          patientId,
        });
      } catch (error) {
        console.error("Error cancelling appointment:", error);
      } finally {
        setLoadingAppointments((prev) => ({
          ...prev,
          [cancelledAppointmentId]: false,
        }));
      }
    };

    // Get status color based on the callStatusMap
    const getStatusColor = (status: string, id?: string) => {
      const callStatus = id && callStatusMap[id] !== undefined ? callStatusMap[id] : status;
      switch (callStatus) {
        case "nicht benötigt":
        case "erledigt":
          return mobiliTheme.palette.success.main;
        case "benötigt":
          return mobiliTheme.palette.secondary.main;
        case "versucht":
          return mobiliTheme.palette.warning.main;
        default:
          return "";
      }
    };

    // Both facts of a cancelled slot's moment share one cell: date on top of
    // time would double the row, side by side they share a line box. Coloured
    // by urgency as before; tabular figures so the column doesn't jitter.
    const dateCell = (originalDate: any) => (
      <Tooltip
        title={`${format(new Date(originalDate), "dd.MM.yyyy")}  ${format(
          new Date(originalDate),
          "HH:mm"
        )}`}
      >
        <span
          className="whitespace-nowrap text-dense font-semibold tabular-nums"
          style={{ color: calculateColor(originalDate) }}
        >
          {format(new Date(originalDate), "dd.MM")}
        </span>
      </Tooltip>
    );

    const timeCell = (originalDate: any) => (
      <span
        className="whitespace-nowrap text-dense font-semibold tabular-nums"
        style={{ color: calculateColor(originalDate) }}
      >
        {format(new Date(originalDate), "HH:mm")}
      </span>
    );

    // The reason decides the follow-up action, so it belongs in the row rather
    // than behind a click into the appointment. It arrives on the list DTO, so
    // showing it costs no extra request.
    const reasonCell = (ca: any) => {
      const reason = (ca?.reason ?? "").trim();

      if (!reason) {
        return (
          <Tooltip title="Kein Stornierungsgrund hinterlegt">
            <span className="text-dense text-ink-muted">—</span>
          </Tooltip>
        );
      }

      return (
        <Tooltip title={reason}>
          <span className="block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-dense text-ink-text">
            {reason}
          </span>
        </Tooltip>
      );
    };

    const callCell = (ca: any, patient: any) => (
      <Tooltip
        title={
          <Typography>
            Status: {ca.callStatus || "nicht benötigt"}
            <br />
            Mobil: {patient.mobile}
            <br />
            Telefon1: {patient.phone1 || "-"}
            <br />
            Telefon2: {patient.phone2 || "-"}
          </Typography>
        }
      >
        <IconButton size="small" onClick={(event) => handleClick(event, ca.id)}>
          <Smartphone
            sx={{
              fontSize: 18,
              color: getStatusColor(ca.callStatus || "nicht benötigt", ca.id),
            }}
          />
        </IconButton>
      </Tooltip>
    );

    const actionsCell = (ca: any, patient: any, patientId: string) => (
      <div className="flex items-center">
        <Tooltip title="Planen">
          <IconButton
            size="small"
            onClick={() =>
              handlePlanning(patientId, moment(ca.originalDate).format("YYYY-MM-DD"))
            }
          >
            <EditCalendar sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Ausfallen lassen">
          <span className="inline-flex items-center">
            {loadingAppointments[ca.id] ? (
              <CircularProgress size={16} sx={{ mx: 0.5 }} />
            ) : (
              <IconButton
                size="small"
                onClick={() => cancellationOfCancelledAppointmentOnClick(ca.id, patient.id)}
              >
                <Cancel className="cancel-icon" sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </span>
        </Tooltip>
      </div>
    );

    return (
      <TableContainer
        sx={{
          // Reduced from 400 to make room for the filter bar above it; the
          // widget card is capped at 440px.
          maxHeight: 356,
          overflow: "auto",
          backgroundColor: mobiliTheme.cardBackground,
        }}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 1,
              background: mobiliTheme.cardBackground,
            }}
          >
            <TableRow>
              <TableCell sx={{ py: 0.5 }}>Patient</TableCell>
              <TableCell sx={{ py: 0.5, width: 52 }}>Datum</TableCell>
              <TableCell sx={{ py: 0.5, width: 52 }}>Uhrzeit</TableCell>
              <TableCell sx={{ py: 0.5 }}>Heilmittel</TableCell>
              <TableCell sx={{ py: 0.5 }}>Grund</TableCell>
              <TableCell sx={{ py: 0.5, width: 56 }}>Ort</TableCell>
              <TableCell sx={{ py: 0.5, width: 72 }}>PT</TableCell>
              <TableCell sx={{ py: 0.5, width: 60 }}>Lücke</TableCell>
              <TableCell sx={{ py: 0.5, width: 48 }}>Anruf</TableCell>
              <TableCell sx={{ py: 0.5, width: 76 }}>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              // Group all cancelled appointments by patientId, then by therapy signature
              const patientGroupMap = new Map<string, any[]>();
              const patientOrder: string[] = [];
              const patientInfoMap = new Map<string, any>();

              visibleAppointments?.forEach((ca: any) => {
                // Guard: skip malformed rows with no patient (would crash the whole table)
                if (!ca?.patient?.id) return;
                const pid = ca.patient.id;
                if (!patientGroupMap.has(pid)) {
                  patientGroupMap.set(pid, []);
                  patientOrder.push(pid);
                  patientInfoMap.set(pid, ca.patient);
                }
                patientGroupMap.get(pid)!.push(ca);
              });

              return patientOrder.map((patientId) => {
                const patientAppts = patientGroupMap.get(patientId)!;
                const patient = patientInfoMap.get(patientId)!;

                // Group by therapy signature within this patient
                const groups = groupCancelledAppointmentsBySignature(patientAppts);

                return (
                  <React.Fragment key={patientId}>
                    {groups.map((group, gIdx) => {
                      const groupKey = `${patientId}::${group.signature}`;
                      const firstCancelled = group.representative;

                      return (
                        <React.Fragment key={groupKey}>
                          {/* Main summary row */}
                          <TableRow
                            sx={{
                              "&:hover": { backgroundColor: "rgb(var(--ink) / 0.02)" },
                              borderLeft: group.count > 1 ? "3px solid" : "none",
                              borderLeftColor: group.count > 1 ? "primary.main" : "transparent",
                            }}
                          >
                            {/* PATIENT CELL – only on first group. The floating
                                per-patient count badge is gone: the ×N on each
                                group row already carries the stacking. */}
                            <TableCell sx={{ py: 0.5 }}>
                              {gIdx === 0 ? (
                                <Typography
                                  onClick={() => handlePatientData(patient)}
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {`${patient.firstName} ${patient.lastName}`}
                                </Typography>
                              ) : null}
                            </TableCell>

                            <TableCell sx={{ py: 0.5, width: 52 }}>
                              {dateCell(firstCancelled.originalDate)}
                            </TableCell>

                            <TableCell sx={{ py: 0.5, width: 52 }}>
                              {timeCell(firstCancelled.originalDate)}
                            </TableCell>

                            {/* THERAPY CELL with stack count */}
                            <TableCell sx={{ py: 0.5 }}>
                              <TherapyCellText
                                timeSlots={firstCancelled.appointment?.timeSlots}
                                stackCount={group.count}
                              />
                            </TableCell>

                            {/* GRUND CELL */}
                            <TableCell sx={{ py: 0.5 }}>{reasonCell(firstCancelled)}</TableCell>

                            {/* ORT CELL */}
                            <TableCell sx={{ py: 0.5, width: 56 }}>
                              {firstCancelled.appointment?.address?.type ? (
                                <VisitTypeBadge
                                  visitType={firstCancelled.appointment?.address?.type}
                                  visitFee={
                                    firstCancelled.appointment?.timeSlots?.find(
                                      (slot: any) => slot?.visitFee?.abbreviation
                                    )?.visitFee?.abbreviation || null
                                  }
                                />
                              ) : (
                                <ErrorOutline sx={{ color: mobiliTheme.palette.info.light }} />
                              )}
                            </TableCell>

                            {/* PT CELL */}
                            <TableCell sx={{ py: 0.5, width: 72 }}>
                              <div className="flex flex-wrap items-center gap-0.5">
                                {firstCancelled?.users?.map((user: UserDto, index: any) => (
                                  <MobiliAvatar
                                    key={user.id || index}
                                    user={user}
                                    sx={{ height: 22, width: 22, fontSize: "0.65rem" }}
                                    isTherapist
                                  />
                                ))}
                              </div>
                            </TableCell>

                            {/* LÜCKE CELL */}
                            <TableCell sx={{ py: 0.5, width: 60 }}>
                              <GapDays
                                gapInDays={firstCancelled.gapInDays}
                                firstMatchesCancelled={
                                  firstCancelled?.firstLastCancelled?.firstMatchesCancelled
                                }
                                lastMatchesCancelled={
                                  firstCancelled?.firstLastCancelled?.lastMatchesCancelled
                                }
                              />
                            </TableCell>

                            {/* ANRUF CELL */}
                            <TableCell sx={{ py: 0, width: 48 }}>
                              {callCell(firstCancelled, patient)}
                            </TableCell>

                            {/* AKTIONEN CELL */}
                            <TableCell sx={{ py: 0, width: 76 }}>
                              {actionsCell(firstCancelled, patient, patientId)}
                            </TableCell>
                          </TableRow>

                          {/* Additional rows for stacked identical appointments */}
                          {group.count > 1 &&
                            group.appointments.slice(1).map((ca: any, aIdx: number) => (
                              <TableRow
                                key={`${groupKey}-${aIdx}`}
                                sx={{
                                  backgroundColor: "rgb(var(--ink) / 0.03)",
                                  borderLeft: "3px solid",
                                  borderLeftColor: "primary.light",
                                  "& td": { py: 0.25 },
                                }}
                              >
                                <TableCell />
                                <TableCell sx={{ width: 52 }}>
                                  {dateCell(ca.originalDate)}
                                </TableCell>
                                <TableCell sx={{ width: 52 }}>
                                  {timeCell(ca.originalDate)}
                                </TableCell>
                                <TableCell>
                                  <TherapyCellText timeSlots={ca.appointment?.timeSlots} />
                                </TableCell>
                                {/* Stacked rows share a therapy signature, not a
                                    reason — each carries its own. */}
                                <TableCell>{reasonCell(ca)}</TableCell>
                                <TableCell sx={{ width: 56 }}>
                                  {ca.appointment?.address?.type ? (
                                    <VisitTypeBadge
                                      visitType={ca.appointment?.address?.type}
                                      visitFee={
                                        ca.appointment?.timeSlots?.find(
                                          (slot: any) => slot?.visitFee?.abbreviation
                                        )?.visitFee?.abbreviation || null
                                      }
                                    />
                                  ) : (
                                    <ErrorOutline
                                      sx={{ color: mobiliTheme.palette.info.light }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell sx={{ width: 72 }}>
                                  <div className="flex flex-wrap items-center gap-0.5">
                                    {ca?.users?.map((user: UserDto, idx: any) => (
                                      <MobiliAvatar
                                        key={user.id || idx}
                                        user={user}
                                        sx={{ height: 22, width: 22, fontSize: "0.65rem" }}
                                        isTherapist
                                      />
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell sx={{ width: 60 }}>
                                  <GapDays
                                    gapInDays={ca.gapInDays}
                                    firstMatchesCancelled={
                                      ca?.firstLastCancelled?.firstMatchesCancelled
                                    }
                                    lastMatchesCancelled={
                                      ca?.firstLastCancelled?.lastMatchesCancelled
                                    }
                                  />
                                </TableCell>
                                <TableCell sx={{ py: 0, width: 48 }}>
                                  {callCell(ca, patient)}
                                </TableCell>
                                <TableCell sx={{ py: 0, width: 76 }}>
                                  {actionsCell(ca, patient, patientId)}
                                </TableCell>
                              </TableRow>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              });
            })()}
          </TableBody>
        </Table>
        {/* One shared menu for every Anruf button — the row it acts on is
            carried by selectedAppointmentId, so it must not live inside the
            table markup. */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          {callStatusOptions.map((option) => (
            <MenuItem
              key={option.status}
              onClick={() => handleCallStatusSelect(option.status)}
              sx={{ color: getStatusColor(option.status) }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      </TableContainer>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ px: 1, pb: 0.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={visitFilter}
            onChange={(_event, next: VisitFilter | null) => {
              // MUI reports null when the active button is clicked again;
              // keeping the current filter avoids an unlabelled empty state.
              if (next !== null) setVisitFilter(next);
            }}
            aria-label="Stornierungen nach Behandlungsort filtern"
          >
            {VISIT_FILTERS.map((filter) => (
              <ToggleButton
                key={filter.value}
                value={filter.value}
                sx={{ py: 0.25, px: 1, textTransform: "none", fontSize: "12px" }}
              >
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {!cancelledAppointments ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          renderAppointmentTable()
        )}
      </Grid>
    </Grid>
  );
});

export default CancelledAppointments;
